/**
 * Dependencies.
 */
import { authService } from '@prz-dp/services';
import Moment from 'moment-timezone';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';

import { orderRequestService } from '@/api/order-request/order-request.service';
import { mqttService } from '@/modules/main/services/mqtt.service';
import {
  ORDER_REQUEST_STATUS,
  ORDER_REQUEST_TYPES,
  TRACKING_ERRORS,
  TRACKING_STATUS
} from '@/modules/tracking/constants';
import { trackingStore } from '@/modules/tracking/store';

/**
 * Mixin.
 */
@Component({
  name: 'tracking-mixin'
})
export default class extends Vue {
  /**
   * Property `moment`.
   */
  moment = Moment;

  /**
   * Property `error`.
   */
  error = false;

  /**
   * Property `errorMessage`.
   */
  errorMessage = null;

  /**
   * Property `isClientRequestModalVisible`.
   */
  isClientRequestModalVisible = false;

  /**
   * Property `hasChatMessageArrived`.
   */
  hasChatMessageArrived = false;

  /**
   * Property `clientRequest`.
   */
  clientRequest: {
    status?: string;

    type?: string;
  } = null;

  /**
   * Getter `key`.
   */
  get key() {
    const { key = null } = this.$route.params || {};

    return key;
  }

  /**
   * Getter order.
   */
  get order() {
    return trackingStore.order;
  }

  /**
   * Getter order type.
   */
  get orderType() {
    return trackingStore.orderType;
  }

  /**
   * Getter `isDelivery`.
   */
  get isDelivery() {
    return trackingStore.isDelivery;
  }

  /**
   * Getter `isCollect`.
   */
  get isCollect() {
    return trackingStore.isCollect;
  }

  /**
   * Getter `trackingNumber`.
   */
  get trackingNumber() {
    const { trackingNumber = null } = trackingStore || {};

    return trackingNumber;
  }

  /**
   * Getter `clientRequestNotification`;
   */
  get clientRequestNotification() {
    const { status = null, type = null } = this.clientRequest || {};

    return { status, type };
  }

  /**
   * Property `requestPendingTreatment`.
   */
  get requestPendingTreatment() {
    return trackingStore.requestPendingTreatment;
  }

  /**
   * Property `hasPendingContact`.
   */
  get hasPendingContact() {
    const { contact = false } = this.requestPendingTreatment || {};

    return contact;
  }

  /**
   * Property `hasPendingDeliveryChange`.
   */
  get hasPendingDeliveryChange() {
    const {
      changeAddress = false,
      changeDate = false,
      changeDateAndAddress = false
    } = this.requestPendingTreatment || {};

    return changeDate || changeAddress || changeDateAndAddress;
  }

  /**
   * Getter has request message.
   */
  get hasRequestMessage() {
    const requests = trackingStore.requests || [];

    const requestMessagePending = requests.find(
      ({ type }) => type === ORDER_REQUEST_TYPES.MESSAGE
    );

    return !!requestMessagePending;
  }

  /**
   * Getter is authenticated.
   */
  get isAuthenticated() {
    const { auth = null } = this.$store?.state || {};
    const { authenticated = false } = auth || {};

    return authenticated;
  }

  /**
   * Getter `isServicePoint`.
   */
  get isServicePoint() {
    const order = this.isDelivery ? this.delivery : this.collect;
    const { address } = order || {};
    const { servicePoint } = address || {};
    const { code = null } = servicePoint || {};

    return !!code;
  }

  /**
   * Getter `isToCollectInServicePoint`
   */
  get isToCollectInServicePoint() {
    return this.isCollect && this.isServicePoint;
  }

  /**
   * Getter `servicePointRetentionTime`.
   */
  get servicePointRetentionTime() {
    const { servicePointRetentionTime } = trackingStore || {};

    return servicePointRetentionTime;
  }

  /**
   * Getter message order request id.
   */
  get messageOrderRequestId() {
    const requests = trackingStore.requests || [];

    const requestMessagePending = requests.find(
      ({ type }) => type === ORDER_REQUEST_TYPES.MESSAGE
    );

    const { id: orderRequestId = null } = requestMessagePending || {};

    return orderRequestId;
  }

  /**
   * Getter current order id.
   */
  get currentOrderId() {
    const { id } = this.order || {};

    return id;
  }

  /**
   * Getter has current delivery payment pending.
   */
  get hasCurrentDeliveryPaymentPending() {
    let hasPaymentPending = false;

    if (this.isDelivery) {
      const { paymentPending = false } = trackingStore.delivery || {};

      hasPaymentPending = paymentPending;
    }

    return hasPaymentPending;
  }

  /**
   * Getter has delivery expiring packages.
   */
  get hasDeliveryExpiringPackages() {
    let hasExpiringPackages = false;

    if (this.isDelivery) {
      const { packages = [] } = trackingStore.delivery || {};
      const deliveryPackage = packages.find(
        ({ expirationDate = null }) => !!expirationDate
      );

      hasExpiringPackages = deliveryPackage ? true : false;
    }

    return hasExpiringPackages;
  }

  /**
   * Getter `canChangeAddressToServicePoint`.
   */
  get canChangeAddressToServicePoint() {
    return (
      !this.hasDeliveryExpiringPackages &&
      !this.hasCurrentDeliveryPaymentPending
    );
  }

  /*
   * Getter fake processing date.
   */
  get fakeProcessingDate() {
    const [first = null] = trackingStore.tracking || [];
    const { hideHistory = false } = first || {};

    let date = null;

    if (hideHistory) {
      const { NOT_COLLECTED, NOT_DELIVERED, PROCESSING } = TRACKING_STATUS;
      const [firstItem, secondItem] = trackingStore.tracking;

      const { status: firstItemStatus } = firstItem || {};
      const { status: secondItemStatus } = secondItem || {};

      const { code: firstItemStatusCode } = firstItemStatus || {};
      const { code: secondItemStatusCode } = secondItemStatus || {};

      if (
        firstItemStatusCode === PROCESSING &&
        (secondItemStatusCode === NOT_DELIVERED ||
          secondItemStatusCode === NOT_COLLECTED)
      ) {
        const { CHANGE_DATE, CHANGE_DATE_AND_ADDRESS } = ORDER_REQUEST_TYPES;
        const { AUTOMATIC_PENDING_TREATMENT, TREATED } = ORDER_REQUEST_STATUS;
        const { requests } = trackingStore;
        const requestItem = requests.find(
          ({ type, status: { code = null } = {} }) =>
            (code === AUTOMATIC_PENDING_TREATMENT || code === TREATED) &&
            (type === CHANGE_DATE || type === CHANGE_DATE_AND_ADDRESS)
        );

        const { request = null } = requestItem || {};
        const { to = null } = request || {};
        const { date: requestDate = null } = to || {};

        if (requestDate) {
          date = requestDate;
        }
      }
    }

    return date;
  }

  /*
   * Getter `canCreateRequest`.
   */
  get canCreateRequest() {
    return trackingStore.canCreateRequests && !this.isServicePoint;
  }

  /*
   * Getter `canCreateChangeRequest`.
   */
  get canCreateChangeRequest() {
    return this.canCreateRequest && !this.hasPendingDeliveryChange;
  }

  /**
   * Getter `trackingRequests`.
   */
  get trackingRequests() {
    return trackingStore.requests;
  }

  /**
   * Getter `collect`.
   */
  get collect() {
    return trackingStore.collect;
  }

  /**
   * Getter client.
   */
  get client() {
    return this.isDelivery
      ? this.delivery?.client?.name
      : this.collect?.order?.client?.name;
  }

  /**
   * Getter `delivery`.
   */
  get delivery() {
    return trackingStore.delivery;
  }

  /**
   * Getter `expectedDeliveryDate`.
   */
  get expectedDeliveryDate() {
    const { expectedDeliveryDate = null } = this.delivery || {};

    return expectedDeliveryDate;
  }

  /**
   * Getter `numberOfPackages`.
   */
  get numberOfPackages() {
    const { numberPackages } = this.order || {};

    return numberPackages;
  }

  /**
   * Getter `isOrderProcessDelayed`.
   */
  get isOrderProcessDelayed() {
    return trackingStore.orderProcessDelayed;
  }

  /**
   * Handle initialize mixin.
   */
  async handleInitializeMixin() {
    const { tracking = null } = trackingStore || {};

    if (!tracking) {
      await this.handleGetDeliveryData();
      this.handleMqttSubscribeTopics();
    }

    mqttService.onMessageArrivedCallback = this.handleMqttTopicMessageArrived;
  }

  /**
   * Handle get delivery data.
   */
  async handleGetDeliveryData() {
    this.isLoading = true;

    const alreadyHaveData = !!trackingStore.tracking;

    try {
      await this.handleValidateUser();
      await trackingStore.getData(this.key);
    } catch (error) {
      const errorCode = error?.raw?.code;

      if (errorCode === TRACKING_ERRORS.TRACKING_NOT_FOUND) {
        this.$router.push({ name: 'NotFound' });
      } else if (errorCode === TRACKING_ERRORS.INVALID_TRACKING_NUMBER) {
        this.$router.push({ name: 'InvalidTrackingNumber' });
      } else {
        this.errorMessage = this.i18n('MAIN.ERRORS.BASE');
        this.error = true;
      }
    } finally {
      this.isLoading = false;

      // Get distribution center time zone.
      const { timeZone = null } = trackingStore?.distributionCenter || {};
      if (timeZone) {
        this.moment.tz.setDefault(timeZone);
      }

      if (alreadyHaveData) {
        this.$emit('trackingDataUpdated');
      }
    }
  }

  /**
   * Handle get request messages.
   */
  async handleGetRequestMessages({ deliveryRequestId }) {
    let messages = [];

    try {
      const response = await orderRequestService.getById(deliveryRequestId);
      const { messages: requestMessages = [] } = response || {};

      messages = requestMessages;
    } catch (error) {
      if (process.env.BUILD_MODE !== 'prd') {
        console.error(error);
      }
    }

    return messages;
  }

  /**
   * Handle get session uuid.
   */
  handleGetSessionUuid(trackingNumber: string) {
    let sessionUuid = '';

    try {
      const userAgent = navigator.userAgent.toLowerCase();

      const isAppProzisStore = userAgent.includes('appprozisstore');
      const isAndroidWebView = / wv\).+(chrome)\/([\w\.]+)/i.test(userAgent);
      const isIosWebView =
        /iphone|ipod|ipad/.test(userAgent) && !/safari/.test(userAgent);

      // Get default uuid.
      const defaultUuid = localStorage.getItem('defaultTrackingUuid');

      if (isAppProzisStore || isAndroidWebView || isIosWebView) {
        // Is from AppProzisStore or WebView.
        sessionUuid = defaultUuid;

        if (!sessionUuid) {
          // Default uuid doesn't exist, create new one.
          sessionUuid = Number(new Date()).toString();

          localStorage.setItem('defaultTrackingUuid', sessionUuid);
        }
      } else {
        // From browser.
        // Get session uuid.
        sessionUuid = sessionStorage.getItem('sessionTrackingUuid');

        // Remove session uuid so that a duplicate tab doesn't use the same session uuid.
        sessionStorage.removeItem('sessionTrackingUuid');

        // Get current watching tracking numbers.
        const currentTrackingNumbers: { [key: string]: number } =
          JSON.parse(localStorage.getItem('currentTrackingNumbers')) || {};

        const trackingNumberTabs = currentTrackingNumbers[trackingNumber] || 0;

        if (!sessionUuid && defaultUuid && !trackingNumberTabs) {
          // Session uuid doesn't exist and default uuid exist.
          // There is currently no other tab with this tracking number.
          sessionUuid = defaultUuid;
        } else if (!sessionUuid) {
          // Session uuid and default uuid doesn't exist, create a new uuid.
          sessionUuid = Number(new Date()).toString();
        }

        if (!defaultUuid) {
          // Default uuid doesn't exist, set the same as session uuid.
          localStorage.setItem('defaultTrackingUuid', sessionUuid);
        }

        // Reinitialize current watching tracking numbers.
        localStorage.setItem(
          'currentTrackingNumbers',
          JSON.stringify({ [trackingNumber]: 1 })
        );

        // Force other tabs to update current watching tracking numbers.
        localStorage.setItem('lastAcess', Number(new Date()).toString());

        window.addEventListener('storage', e => {
          if (e.key == 'lastAcess') {
            try {
              const currentTrackingNumbers: { [key: string]: number } =
                JSON.parse(localStorage.getItem('currentTrackingNumbers')) ||
                {};

              const trackingNumberTabs =
                currentTrackingNumbers[trackingNumber] || 0;

              const newNumberOfTabs = trackingNumberTabs + 1;

              currentTrackingNumbers[trackingNumber] = newNumberOfTabs;

              localStorage.setItem(
                'currentTrackingNumbers',
                JSON.stringify(currentTrackingNumbers)
              );
            } catch (error) {
              if (process.env.BUILD_MODE !== 'prd') {
                console.error(error);
              }
            }
          }
        });

        // Remove tab from the current watching tracking numbers.
        let alreadyRunUnload = false;

        const unloadCallback = () => {
          try {
            if (!alreadyRunUnload) {
              alreadyRunUnload = true;

              const currentTrackingNumbers: { [key: string]: number } =
                JSON.parse(localStorage.getItem('currentTrackingNumbers')) ||
                {};

              const trackingNumberTabs =
                currentTrackingNumbers[trackingNumber] || 0;

              const newNumberOfTabs =
                trackingNumberTabs > 0
                  ? trackingNumberTabs - 1
                  : trackingNumberTabs;

              if (!newNumberOfTabs) {
                delete currentTrackingNumbers[trackingNumber];
              } else {
                currentTrackingNumbers[trackingNumber] = newNumberOfTabs;
              }

              localStorage.setItem(
                'currentTrackingNumbers',
                JSON.stringify(currentTrackingNumbers)
              );

              // Set session uuid to reuse the same uuid in case of refresh.
              sessionStorage.setItem('sessionTrackingUuid', sessionUuid);
            }
          } catch (error) {
            if (process.env.BUILD_MODE !== 'prd') {
              console.error(error);
            }
          }
        };

        window.addEventListener('beforeunload', unloadCallback);
        window.addEventListener('unload', unloadCallback);
      }
    } catch (error) {
      if (process.env.BUILD_MODE !== 'prd') {
        console.error(error);
      }

      sessionUuid = Number(new Date()).toString();
    }

    return sessionUuid;
  }

  /**
   * Handle mqtt subscribe topics.
   */
  handleMqttSubscribeTopics() {
    try {
      // Connect to mqtt server.
      const sessionUuid = this.handleGetSessionUuid(this.trackingNumber);

      const clientId = `prozis-now-customer-ui-${process.env.BUILD_MODE}-${this.trackingNumber}-${sessionUuid}`;

      mqttService.connect(clientId);

      // Subscribe topics.
      const [currentTracking] = trackingStore.tracking || [];
      const { status: { code = null } = {} } = currentTracking || {};
      const { code: routeCode = null } = trackingStore.route || {};
      const isInRouting = code === TRACKING_STATUS.IN_ROUTING;
      const isVisitedSuccessfully =
        code === TRACKING_STATUS.DELIVERED ||
        code === TRACKING_STATUS.COLLECTED;

      const topicsToSubscribe: string[] = [];

      // Can show vehicle.
      const { canShowVehicle } = trackingStore || {};

      if (isInRouting && canShowVehicle) {
        topicsToSubscribe.push(`route/${routeCode}/location`);
      }

      if (!isVisitedSuccessfully) {
        topicsToSubscribe.push(
          `route/${routeCode}/${this.orderType.toLowerCase()}/info`,
          `route/${routeCode}/request/info`
        );
      }

      if (topicsToSubscribe.length) {
        mqttService.subscribe(topicsToSubscribe);
      }
    } catch (error) {
      if (process.env.BUILD_MODE !== 'prd') {
        console.error(error);
      }
    }
  }

  /**
   * Handle mqtt topic message arrived.
   */
  handleMqttTopicMessageArrived({ payload, topic }) {
    const { code: routeCode = null } = trackingStore.route || {};

    switch (topic) {
      case `route/${routeCode}/location`:
        trackingStore.setVehicleCoordinatesPayload(payload);
        break;

      case `route/${routeCode}/request/info`:
      case `route/${routeCode}/${this.orderType.toLowerCase()}/info`:
        this.handleMqttDeliveryMessageType({ payload });
        break;
    }
  }

  /**
   * Handle mqtt message type.
   */
  handleMqttDeliveryMessageType({ payload }) {
    const { code: routeCode = null } = trackingStore.route || {};
    const { message = null, type: payloadType = null } = payload || {};
    const {
      collect = null,
      delivery = null,
      deliveryRequestId = null,
      status = null,
      type = null
    } = message || {};
    const { trackingNumber: messageTrackingNumber = null } =
      delivery || collect || {};
    const { code: statusCode = null } = status || {};

    switch (payloadType) {
      case 'STATUS':
        if (this.trackingNumber === messageTrackingNumber) {
          this.handleDeliveryStatusChanged({
            routeCode,
            statusCode
          });
        }
        break;

      case 'START_TRIP':
        this.handleTripStarted();
        break;

      case 'CLIENT_REQUEST':
        this.handleClientRequestUpdate({
          deliveryRequestId,
          status,
          type
        });
        break;

      case 'CHAT_MESSAGE_CREATE':
        this.handleChatMessage({ message });
        break;

      case 'CHAT_MESSAGE_DELETE':
        this.handleChatMessage({ message, type: 'deleted' });
        break;

      case 'CHAT_MESSAGE_READ':
        this.handleChatMessage({ message, type: 'readed' });
        break;
    }
  }

  /**
   * Handle delivery status changed.
   */
  async handleDeliveryStatusChanged({
    routeCode,
    statusCode
  }: {
    routeCode: string;
    statusCode: string;
  }) {
    const isInRouting = statusCode === TRACKING_STATUS.IN_ROUTING;
    const isVisitedSuccessfully =
      statusCode === TRACKING_STATUS.DELIVERED ||
      statusCode === TRACKING_STATUS.COLLECTED;

    const topicsToSubscribe: string[] = [];
    const topicsToUnsubscribe: string[] = [];

    // Update tracking information.
    await this.handleGetDeliveryData();

    // Can show vehicle.
    const { canShowVehicle } = trackingStore || {};

    if (isInRouting && canShowVehicle) {
      topicsToSubscribe.push(`route/${routeCode}/location`);
    }

    // Unsubscribe topics.
    if (!isInRouting) {
      topicsToUnsubscribe.push(`route/${routeCode}/location`);

      trackingStore.setVehicleCoordinatesPayload(null);
    }

    if (isVisitedSuccessfully) {
      topicsToUnsubscribe.push(
        `route/${routeCode}/${this.orderType.toLowerCase()}/info`,
        `route/${routeCode}/request/info`
      );
    }

    if (topicsToSubscribe.length) {
      mqttService.subscribe(topicsToSubscribe);
    }

    if (topicsToUnsubscribe.length) {
      mqttService.unsubscribe(topicsToUnsubscribe);
    }
  }

  /**
   * Handle trip started.
   */
  async handleTripStarted() {
    const { canShowVehicle: oldCanShowVehicle, route } = trackingStore || {};
    const { code: routeCode = null } = route || {};

    const topicsToSubscribe: string[] = [];

    await this.handleGetDeliveryData();

    const { canShowVehicle } = trackingStore || {};

    if (!oldCanShowVehicle && canShowVehicle) {
      topicsToSubscribe.push(`route/${routeCode}/location`);
    }

    if (topicsToSubscribe.length) {
      mqttService.subscribe(topicsToSubscribe);
    }
  }

  /**
   * Handle client request update.
   */
  handleClientRequestUpdate({ deliveryRequestId, status, type }) {
    if (this.isAuthenticated) {
      switch (type) {
        case ORDER_REQUEST_TYPES.MESSAGE:
          this.handleClientRequestClose();
          break;

        default:
          const hasTrackingRequest = this.trackingRequests.some(
            ({ id }) => id === deliveryRequestId
          );

          this.isClientRequestModalVisible = false;

          setTimeout(() => {
            if (hasTrackingRequest) {
              this.clientRequest = {
                status,
                type
              };

              this.isClientRequestModalVisible = true;
            }
          }, 500);
          break;
      }
    }
  }

  /**
   * Handle chat message.
   */
  handleChatMessage({ message, type = null }) {
    const {
      collectId = null,
      deliveryId = null,
      deliveryRequestId,
      id,
      isRead = false,
      message: requestMessage,
      source
    } = message || {};

    if (this.messageOrderRequestId === deliveryRequestId) {
      this.hasChatMessageArrived = true;

      trackingStore.addChatMessage({
        ...(this.isDelivery ? { deliveryId } : { collectId }),
        deliveryRequestId,
        id,
        isRead,
        message: requestMessage,
        recordDate: this.moment().toISOString(),
        source: type ? type.toUpperCase() : source
      });
    }
  }

  /**
   * Handle validate user.
   */
  async handleValidateUser() {
    if (this.isAuthenticated) {
      try {
        const {
          result: isValidUser = false
        } = await orderRequestService.validateUser(this.key);

        if (!isValidUser) {
          await this.handleLogout();
        }
      } catch (error) {
        await this.handleLogout();
      }
    }
  }

  /**
   * Handle client request close.
   */
  async handleClientRequestClose() {
    this.isClientRequestModalVisible = false;

    await this.handleGetDeliveryData();
  }

  /**
   * Handle logout.
   */
  async handleLogout() {
    await authService.logout({
      redirectUri: `${window.location.origin}/unauthorized`
    });
  }

  /**
   * Handle force tracking scene.
   */
  handleForceTrackingScene() {
    if (this.$route.name !== 'Tracking') {
      this.$router.replace({
        name: 'Tracking',
        params: {
          key: this.key
        }
      });
    }
  }

  /**
   * Handle filter service points.
   */
  handleFilterServicePoints(servicePoints) {
    if (this.isCollect) {
      servicePoints = servicePoints.filter(({ configs }) => {
        const { ServicePointAcceptCollects = false } = configs || {};

        return ServicePointAcceptCollects === 'true';
      });
    }

    const { dayChanged: newDate } = trackingStore || {};
    const { expectedDeliveryDate } = this.delivery || {};
    const { expectedCollectDate } = this.collect || {};
    const expectedOrderDate = this.isDelivery
      ? expectedDeliveryDate
      : expectedCollectDate;

    const weekDays = [7, 1, 2, 3, 4, 5, 6];
    const today = this.moment().startOf('day');

    const expectedIsAfterToday = this.moment(expectedOrderDate)
      .startOf('day')
      .isAfter(today);

    const weekDay = newDate
      ? weekDays[this.$moment(newDate).day()]
      : expectedIsAfterToday
        ? weekDays[this.$moment(expectedOrderDate).day()]
        : weekDays[today.day()];

    servicePoints = servicePoints.filter(({ dockingDays, workDays }) => {
      const hasDockingDay = (dockingDays || {}).hasOwnProperty(`${weekDay}`)
        ? !!dockingDays[`${weekDay}`]?.isActive
        : false;
      const hasWorkingDay = (workDays || {}).hasOwnProperty(`${weekDay}`)
        ? !!workDays[`${weekDay}`]?.isActive
        : false;

      return hasDockingDay || hasWorkingDay;
    });

    return servicePoints;
  }
}
